/**
 * Type definitions for integration providers and Logfire tokens.
 */

import type { ComponentType } from "react";

export type IntegrationStatus = "active" | "coming_soon" | "disabled";

export interface Integration {
  id: string;
  name: string;
  description: string;
  status: IntegrationStatus;
  logo?: ComponentType<{ className?: string }>;
  docsUrl?: string;
  featureFlag?: string;
}

// Logfire Token Types
export interface LogfireTokenCreate {
  read_token: string;
  project_name: string;
  project_url?: string | null;
  region: "us" | "eu";
}

export interface LogfireTokenRead {
  id: string;
  user_id: string;
  project_name: string;
  project_url: string | null;
  is_active: boolean;
  last_sync_at: string | null;
  last_error: string | null;
  consecutive_errors: number;
  pull_interval_minutes: number;
  created_at: string;
  updated_at: string;
}

export interface LogfireTokenValidation {
  is_valid: boolean;
  message: string;
  sample_data?: unknown;
  records_found: number;
}

export interface LogfireTokenCreateResponse extends LogfireTokenRead {
  validation: LogfireTokenValidation;
}

export interface PaginatedLogfireTokens {
  data: LogfireTokenRead[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
  total_pages: number;
}

// Import logo components
import { LogfireLogo, LangChainLogo, AWSBedrockLogo, TeamsLogo, JiraLogo, SlackLogo, GoogleCloudLogo } from "@/components/integrations/logos";

// Integration definitions constant
export const INTEGRATIONS: Integration[] = [
  {
    id: "logfire",
    name: "Logfire",
    description: "Monitor and analyze LLM application logs in real-time with Pydantic's observability platform",
    status: "active",
    logo: LogfireLogo,
    docsUrl: "https://logfire.pydantic.dev/docs/",
    featureFlag: "security_dashboard",
  },
  {
    id: "teams",
    name: "Microsoft Teams",
    description: "Send alert notifications to Microsoft Teams channels via Power Automate webhooks",
    status: "active",
    logo: TeamsLogo,
    docsUrl: "https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook",
  },
  {
    id: "jira",
    name: "Jira",
    description: "Connect your Jira Cloud instance to create and track security issue tickets",
    status: "active",
    logo: JiraLogo,
    docsUrl: "https://support.atlassian.com/jira-cloud/",
    featureFlag: "security_dashboard",
  },
  {
    id: "slack",
    name: "Slack",
    description: "Receive alert notifications in your Slack channels via Incoming Webhooks",
    status: "active",
    logo: SlackLogo,
    docsUrl: "https://api.slack.com/messaging/webhooks",
    featureFlag: "security_dashboard",
  },
  {
    id: "langchain",
    name: "LangChain",
    description: "Integrate with LangChain framework for advanced LLM application development",
    status: "coming_soon",
    logo: LangChainLogo,
    docsUrl: "https://python.langchain.com/docs/",
  },
  {
    id: "aws_cloudwatch",
    name: "AWS CloudWatch",
    description: "Connect your AWS account to ingest Bedrock and SageMaker model invocation logs for security monitoring",
    status: "active",
    logo: AWSBedrockLogo,
    docsUrl: "https://aws.amazon.com/cloudwatch/",
    featureFlag: "security_dashboard",
  },
  {
    id: "aws_shadow_ai",
    name: "Shadow AI Discovery",
    description: "Connect one read-only AWS role and discover every AI model, endpoint, and agent running in your account — Bedrock, SageMaker, and AI-key signals",
    status: "active",
    logo: AWSBedrockLogo,
    docsUrl: "https://aws.amazon.com/cloudformation/",
    featureFlag: "security_dashboard",
  },
  {
    id: "gemini_agent_platform",
    name: "Gemini Enterprise Agent Platform",
    description: "Monitor AI agents running on Google's Gemini Enterprise Agent Platform via BigQuery Observability Analytics",
    status: "active",
    logo: GoogleCloudLogo,
    docsUrl: "https://cloud.google.com/vertex-ai/generative-ai/docs/model-garden/explore-models",
    featureFlag: "security_dashboard",
  },
];
